import React, { useState } from 'react';
import { Spin, Tabs, Modal, Checkbox, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useProductForm } from './hooks/useProductForm';
import { useModulePermission } from '../../hooks/usePermission';
import Tab1ProductSpec from './components/Tab1ProductSpec';
import Tab2CustomerCodes from './components/Tab2CustomerCodes';
import Tab3Image from './components/Tab3Image';
import Tab4CommercialCode from './components/Tab4CommercialCode';
import type { F113 } from '../../services/p1113Api';
import LookupField from '../../components/LookupField';
import './P1113Form.css';

// ── PermBtn ───────────────────────────────────────────────────
const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) return <Tooltip title="權限不足">{btn}</Tooltip>;
  return btn;
};

// ── Helpers ───────────────────────────────────────────────────
const dateToDisplay = (v: string | null | undefined): string => {
  if (!v) return '-';
  return v.substring(0, 10);
};

// ── Component ─────────────────────────────────────────────────
const P1113Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('1');
  const [searchPk1, setSearchPk1] = useState('');
  const [searchPk2, setSearchPk2] = useState('');
  const perms = useModulePermission('pw1113f1');

  const {
    mode, isEditing, loading, record, formData,
    allIds, currentIdx,
    customerCodes, setCustomerCodes,
    compositions, setCompositions,
    specClasses, setSpecClasses,
    getField, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  } = useProductForm();

  const { canView, canCreate, canEdit, canDelete } = perms;

  // ── Action handlers ──
  const handleAddClick = () => {
    if (isEditing) {
      save();
    } else {
      startAdd();
    }
  };

  const handleModifyClick = () => {
    if (isEditing) {
      cancel();
    } else {
      startEdit();
    }
  };

  const handleDeleteClick = () => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${record.f113_01} ${record.f113_02}」（${record.f113_11}）？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleSearch = () => {
    const pk1 = searchPk1.trim();
    quickSearch(pk1, searchPk2.trim());
  };

  const handleExit = () => navigate(-1);

  // ── Tab items ──
  const tabItems = [
    {
      key: '1',
      label: '產品規格',
      children: (
        <Tab1ProductSpec
          formData={formData}
          isEditing={isEditing}
          mode={mode}
          getField={getField as (f: keyof F113) => unknown}
          setField={setField as (f: keyof F113, v: unknown) => void}
        />
      ),
    },
    {
      key: '2',
      label: `客戶產品編號/用途/規格分類 (${customerCodes.length})`,
      children: (
        <Tab2CustomerCodes
          isEditing={isEditing}
          customerCodes={customerCodes}
          compositions={compositions}
          specClasses={specClasses}
          onCustomerCodesChange={setCustomerCodes}
          onCompositionsChange={setCompositions}
          onSpecClassesChange={setSpecClasses}
        />
      ),
    },
    {
      key: '3',
      label: '對照/圖片',
      children: (
        <Tab3Image
          isEditing={isEditing}
          imageUrl={String(getField('f113_18') || '')}
          setField={setField as (f: keyof F113, v: unknown) => void}
        />
      ),
    },
    {
      key: '4',
      label: '商業代號',
      children: (
        <Tab4CommercialCode
          formData={formData}
          isEditing={isEditing}
          getField={getField as (f: keyof F113) => unknown}
          setField={setField as (f: keyof F113, v: unknown) => void}
        />
      ),
    },
  ];

  const canSave = mode === 'add' ? canCreate : canEdit;

  return (
    <Spin spinning={loading}>
      <div className="p1113-form">
        <div className="page-wrapper">
          {/* Title */}
          <div className="page-title-bar">
            <h1>
              產品資料建檔
              <span className="mode-badge">
                {mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}
                {allIds.length > 0 && ` ${currentIdx + 1}/${allIds.length}`}
              </span>
            </h1>
          </div>

          {/* Header: PK fields + checkbox + date */}
          <div className="header-bar">
            <div className="field-group">
              <label>原料代號</label>
              <LookupField
                lookupType="product"
                value={String(getField('f113_01') || '')}
                onChange={(code) => setField('f113_01', code)}
                disabled={mode !== 'add'}
                style={{ width: 100 }}
                title="搜尋產品"
              />
            </div>
            <div className="field-group">
              <label>工繳代號</label>
              <LookupField
                lookupType="processCode"
                value={String(getField('f113_02') || '')}
                onChange={(code) => setField('f113_02', code)}
                disabled={mode !== 'add'}
                style={{ width: 100 }}
                title="搜尋工繳"
              />
            </div>
            <div className="field-group">
              <Checkbox
                checked={Boolean(getField('f113_39'))}
                onChange={e => setField('f113_39', e.target.checked)}
                disabled={!isEditing}
              >
                代購
              </Checkbox>
            </div>
            <div className="field-group">
              <label>建檔日期</label>
              <input
                value={dateToDisplay(formData.f113_36)}
                disabled
                style={{ width: 110 }}
              />
            </div>
          </div>

          {/* Search bar */}
          <div className="header-bar" style={{ gap: 8 }}>
            <div className="field-group">
              <label>快速定位</label>
              <input
                placeholder="原料代號"
                value={searchPk1}
                onChange={e => setSearchPk1(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                disabled={isEditing}
                maxLength={6}
                style={{ width: 100 }}
              />
              <input
                placeholder="工繳代號"
                value={searchPk2}
                onChange={e => setSearchPk2(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                disabled={isEditing}
                maxLength={6}
                style={{ width: 100 }}
              />
              <button
                className="btn"
                onClick={handleSearch}
                disabled={isEditing}
                style={{ padding: '4px 12px', border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
              >
                定位
              </button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{ marginTop: 8 }}
          />

          {/* Record Info */}
          {record && mode === 'browse' && (
            <div className="record-info">
              建檔: {record.f113_35 || '-'} ({dateToDisplay(record.f113_36)})
              {' | '}修改: {record.f113_91 || '-'} ({dateToDisplay(record.f113_92)})
            </div>
          )}

          {/* Action Bar */}
          <div className="action-bar">
            <div className="action-group">
              <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={navFirst}>最前</PermBtn>
              <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={navPrev}>上一筆</PermBtn>
              <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIds.length - 1} onClick={navNext}>下一筆</PermBtn>
              <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIds.length - 1} onClick={navLast}>最後</PermBtn>
            </div>

            <div className="action-separator" />

            <div className="action-group">
              {/* CMDADD: 新增/存檔 */}
              <PermBtn
                allowed={isEditing ? canSave : canCreate}
                disabled={false}
                className="btn btn-primary"
                onClick={handleAddClick}
              >
                {isEditing ? '存檔' : '新增'}
              </PermBtn>

              {/* CMDMODI: 修改/還原 */}
              <PermBtn
                allowed={isEditing ? true : canEdit}
                disabled={!isEditing && !record}
                onClick={handleModifyClick}
              >
                {isEditing ? '還原' : '修改'}
              </PermBtn>

              {/* CMDDEL */}
              <PermBtn
                allowed={canDelete}
                disabled={isEditing || !record}
                className="btn btn-danger"
                onClick={handleDeleteClick}
              >
                刪除
              </PermBtn>
            </div>

            <div className="action-separator" />

            {/* 離開 */}
            <button className="btn" onClick={handleExit}>離開</button>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default P1113Form;
